import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, forkJoin, Observable, of, Subscription } from 'rxjs';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { ExportDialogComponent } from 'src/app/components/directory-tree/dialogs/export-dialog/export-dialog.component';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { DialogActions } from 'src/app/model/dictionary';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({ providedIn: 'root' })
export class PackagingPartExportService implements OnDestroy {
  private recSubscription?: Subscription;
  private warningDialogSubscription?: Subscription;
  private exportDialogSubscription?: Subscription;
  private exportBackendSubscription?: Subscription;

  constructor(
    private dialog: MatDialog,
    private directoryApiService: DirectoryApiService,
    private importExportApiService: ImportExportApiService,
    private analysisApiService: AnalysisApiService,
    private translateService: TranslocoService,
    private spinner: NgxSpinnerService,
    private snackBar: MatSnackBar,
  ) {}

  exportPackagingParts(packagingPart: PackagingPart, ids: number[], onProceed?: () => void) {
    if (ids.length < 1) {
      return;
    }
    const idsToExport = [...ids];
    this.spinner.show();
    if (packagingPart !== PackagingPart.System && packagingPart !== PackagingPart.Unit) {
      this.openExportDialog(packagingPart, idsToExport);
      return;
    }
    const observables = idsToExport.map(id => this.analysisApiService.isRecyclabilityAnalysisPossible(id, true, packagingPart, undefined, true));
    this.recSubscription?.unsubscribe();
    this.recSubscription = forkJoin(observables).subscribe({
      next: returnList => {
        if (!returnList.includes(false)) {
          this.openExportDialog(packagingPart, idsToExport);
          onProceed?.();
          return;
        }

        const warningKey = packagingPart === PackagingPart.System ? 'warnings.exportIncompletePackagingSystem' : 'warnings.exportIncompletePackagingUnit';

        const data: SimpleDialogData = {
          title: this.translateService.translate('common.text.warning'),
          messages: [this.translateService.translate(warningKey)],
          icon: 'warning',
        };

        const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, getDialogConfig(data, '350px'));

        this.warningDialogSubscription?.unsubscribe();
        this.warningDialogSubscription = dialogRef.afterClosed().subscribe(result => {
          if (result.event === DialogActions.REJECT) {
            this.spinner.hide();
            return;
          }

          this.openExportDialog(packagingPart, idsToExport);
          onProceed?.();
        });
      },
      error: _ => {
        this.spinner.hide();
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getExportErrorDialogData(), '500px'));
      },
    });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(
      this.translateService.translate(messageKey),
      this.translateService.translate('common.buttons.close'),
      {
        duration,
        panelClass: ['app-snackbar', panelClass],
      }
    );
  }

  getExportErrorDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.export.exportError')],
      icon: 'error',
    };
  }

  private async openExportDialog(packagingPart: PackagingPart, ids: number[]): Promise<void> {
    try {
      const { principals, profiles, subProfiles } = await firstValueFrom(
        forkJoin({
          principals: this.directoryApiService.getExternalSecurityPrincipals(true),
          profiles: this.getExportProfiles(packagingPart),
          subProfiles: packagingPart === PackagingPart.System ? this.importExportApiService.getPackagingUnitExportProfiles(true) : of(undefined),
        })
      );

      if (!principals) {
        return;
      }

      const dialogData: any = this.getDialogIdsData(packagingPart, ids);

      dialogData.allPrincipals = principals;
      dialogData.allProfiles = profiles;

      if (packagingPart === PackagingPart.System) {
        dialogData.allSubProfiles = subProfiles;
      }

      const dialogRef = this.dialog.open(ExportDialogComponent, getDialogConfig(dialogData, '50%'));

      this.exportDialogSubscription?.unsubscribe();
      this.exportDialogSubscription = dialogRef.afterClosed().subscribe(result => {
        if (result.event === DialogActions.REJECT) {
          return;
        }

        const exportResult: Observable<any>[] = [];
        const profileId = result.profileId;
        const subprofileId = result.subprofileId ?? null;

        for (const principalId of result.selectedPrincipalIds) {
          for (const packagingId of result.selectedIds) {
            exportResult.push(this.getExportCall(packagingPart, packagingId, principalId, profileId, subprofileId));
          }
        }

        this.exportBackendSubscription = forkJoin(exportResult).subscribe({
          next: _ => this.showSnackbar('dataManagement.directory.dialog.export.exportSuccess','app-success', 3000),
          error: _ => this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getExportErrorDialogData(), '500px')),
        });
      });
    } catch (_) {
      this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getExportErrorDialogData(), '500px'));
    } finally {
      this.spinner.hide();
    }
  }

  private getDialogIdsData(packagingPart: PackagingPart, ids: number[]) {
    switch (packagingPart) {
      case PackagingPart.System:
        return { packagingSystemIds: ids };
      case PackagingPart.Unit:
        return { packagingUnitIds: ids };
      case PackagingPart.Component:
        return { componentIds: ids };
      case PackagingPart.Material:
        return { materialIds: ids };
      default:
        throw new Error(`Unsupported packaging part: ${packagingPart}`);
    }
  }

  private getExportProfiles(packagingPart: PackagingPart): Observable<any[]> {
    switch (packagingPart) {
      case PackagingPart.System:
        return this.importExportApiService.getPackagingSystemExportProfiles(true);
      case PackagingPart.Unit:
        return this.importExportApiService.getPackagingUnitExportProfiles(true);
      case PackagingPart.Component:
        return this.importExportApiService.getPackagingComponentExportProfiles(true);
      case PackagingPart.Material:
        return this.importExportApiService.getCompositeMaterialExportProfiles(true);
      default:
        throw new Error(`Unsupported packaging part: ${packagingPart}`);
    }
  }

  private getExportCall(packagingPart: PackagingPart, id: number, principalId: number, profileId?: number, subprofileId?: number): Observable<any> {
    switch (packagingPart) {
      case PackagingPart.System:
        return this.importExportApiService.exportPackagingSystem(id, principalId, profileId, subprofileId, true);
      case PackagingPart.Unit:
        return this.importExportApiService.exportPackagingUnit(id, principalId, profileId, true);
      case PackagingPart.Component:
        return this.importExportApiService.exportComponents(id, principalId, profileId, true);
      case PackagingPart.Material:
        return this.importExportApiService.exportMaterial(id, principalId, profileId, true);
      default:
        throw new Error(`Unsupported packaging part: ${packagingPart}`);
    }
  }

  ngOnDestroy(): void {
    this.recSubscription?.unsubscribe();
    this.warningDialogSubscription?.unsubscribe();
    this.exportDialogSubscription?.unsubscribe();
    this.exportBackendSubscription?.unsubscribe();
  }
}
